import React, { useEffect, useState, useMemo, useRef } from 'react';
import { collection, query, onSnapshot, doc, updateDoc, orderBy, addDoc, serverTimestamp } from 'firebase/firestore';
import { db, storage } from '../lib/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useAuth } from '../contexts/AuthContext';
import { 
  LifeBuoy, Search, Clock, CheckCircle2, MessageSquare, 
  AlertTriangle, User, Wrench, Mail, Hash, Loader2, 
  Tag, CreditCard, ShieldAlert, ArrowUpRight, X, Send, Camera, Reply, Ban, UserCog, HelpCircle, Inbox, Image as ImageIcon
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import Navbar from '../components/ui/Navbar';
import AdminBar from '../components/ui/adminbar';

import planoFundo from "../assets/planodefundo.png"; 

const AdminSupport = () => {
  const { userProfile } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [filterType, setFilterType] = useState('all');
  
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [replyText, setReplyText] = useState("");
  const [replyImages, setReplyImages] = useState([]);
  const [replyPreviews, setReplyPreviews] = useState([]);
  const replyFileInputRef = useRef(null);
  const chatEndRef = useRef(null);

  useEffect(() => {
    const q = query(collection(db, 'support_tickets'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setTickets(data);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!selectedTicket) return;
    const qMsgs = query(collection(db, 'support_tickets', selectedTicket.id, 'messages'), orderBy('createdAt', 'asc'));
    return onSnapshot(qMsgs, (snap) => {
      setChatMessages(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
  }, [selectedTicket]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatMessages]);

  const filteredTickets = useMemo(() => {
    return tickets.filter(t => {
      const matchSearch = (
        t.userName?.toLowerCase().includes(search.toLowerCase()) || 
        t.protocol?.toLowerCase().includes(search.toLowerCase()) ||
        t.subject?.toLowerCase().includes(search.toLowerCase())
      );
      const matchType = filterType === 'all' ? true : t.userType === filterType;
      return matchSearch && matchType;
    });
  }, [tickets, search, filterType]);

  const handleUpdateStatus = async (ticketId, newStatus, userId, protocol) => {
    try {
      await updateDoc(doc(db, 'support_tickets', ticketId), { 
        status: newStatus,
        resolvedAt: newStatus === 'completed' ? new Date().toISOString() : null,
        resolvedBy: userProfile.name
      });
      await addDoc(collection(db, 'notifications'), {
        userId, title: "Suporte Atualizado", message: `Chamado #${protocol} marcado como ${newStatus}.`,
        type: 'support', category: 'support', read: false, createdAt: serverTimestamp()
      });
      toast.success("Status atualizado!");
      if (newStatus === 'completed') setSelectedTicket(null);
    } catch (error) { toast.error("Erro."); }
  };

  const handleSendAdminReply = async (e) => {
    e.preventDefault();
    if (!replyText.trim() && replyImages.length === 0) return;
    setLoading(true);
    try {
      const replyAttachmentUrls = [];
      for (const file of replyImages) {
        const storageRef = ref(storage, `support_attachments/admin_${Date.now()}_${Math.random().toString(36).substring(7)}`);
        const uploadTask = await uploadBytes(storageRef, file);
        const url = await getDownloadURL(uploadTask.ref);
        replyAttachmentUrls.push(url);
      }
      await addDoc(collection(db, 'support_tickets', selectedTicket.id, 'messages'), {
        text: replyText, senderId: 'admin_central', senderName: "Suporte Socorre Aí", senderType: 'admin',
        attachmentUrls: replyAttachmentUrls, createdAt: serverTimestamp()
      });
      await updateDoc(doc(db, 'support_tickets', selectedTicket.id), { status: 'responded', lastMessageAt: serverTimestamp() });
      await addDoc(collection(db, 'notifications'), {
        userId: selectedTicket.userId, title: "Suporte Respondeu!", message: `Nova mensagem no ticket #${selectedTicket.protocol}`,
        type: 'support', read: false, createdAt: serverTimestamp()
      });
      setReplyText(""); setReplyImages([]); setReplyPreviews([]);
      toast.success("Enviado!");
    } catch (e) { toast.error("Erro."); } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-left overflow-x-hidden">
      <div className="fixed inset-0 -z-10 opacity-5" style={{ backgroundImage: `url(${planoFundo})`, backgroundSize: 'cover' }}></div>
      <Navbar />
      <div className="flex pt-[73px]">
        <AdminBar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
        <main className="flex-1 p-6 lg:p-10 max-w-7xl mx-auto w-full text-left">
          <header className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10 text-left">
            <div>
              <h1 className="text-4xl font-black text-slate-900 uppercase italic tracking-tighter leading-none">Painel <span className="text-blue-600">Suporte</span></h1>
              <div className="flex bg-white p-1 rounded-xl shadow-sm border border-slate-100 mt-4 w-fit">
                {['all', 'client', 'technician'].map(type => (
                  <button key={type} onClick={() => setFilterType(type)} className={`px-4 py-1.5 rounded-lg text-[9px] font-black uppercase italic transition-all ${filterType === type ? 'bg-slate-900 text-white' : 'text-slate-400'}`}>{type}</button>
                ))}
              </div>
            </div>
            <div className="relative w-full md:w-96">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300" size={18} />
              <input type="text" placeholder="BUSCAR..." className="w-full h-14 pl-12 pr-6 bg-white border-none shadow-xl rounded-2xl font-bold text-[11px] uppercase italic outline-none focus:ring-2 focus:ring-blue-500/20" value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
          </header>

          <Tabs defaultValue="pending" className="space-y-8">
            <TabsList className="bg-slate-100/50 p-1.5 rounded-[2rem] shadow-inner w-full flex justify-start overflow-x-auto no-scrollbar gap-2">
               <TabsTrigger value="pending" className="rounded-[1.4rem] font-black uppercase italic text-[9px] px-8 py-3.5 data-[state=active]:bg-red-500 data-[state=active]:text-white">Novos</TabsTrigger>
               <TabsTrigger value="responded" className="rounded-[1.4rem] font-black uppercase italic text-[9px] px-8 py-3.5 data-[state=active]:bg-blue-600 data-[state=active]:text-white">Respondidos</TabsTrigger>
               <TabsTrigger value="completed" className="rounded-[1.4rem] font-black uppercase italic text-[9px] px-8 py-3.5 data-[state=active]:bg-green-600 data-[state=active]:text-white">Concluídos</TabsTrigger>
            </TabsList>
            {['pending', 'responded', 'completed'].map(status => (
              <TabsContent key={status} value={status} className="grid gap-4">
                {filteredTickets.filter(t => (t.status || 'pending') === status).map(ticket => (
                  <TicketCard key={ticket.id} ticket={ticket} onOpenChat={() => setSelectedTicket(ticket)} onComplete={() => handleUpdateStatus(ticket.id, 'completed', ticket.userId, ticket.protocol)} />
                ))}
              </TabsContent>
            ))}
          </Tabs>
        </main>
      </div>

      <Dialog open={!!selectedTicket} onOpenChange={() => setSelectedTicket(null)}>
        <DialogContent className="rounded-[3rem] border-none p-0 bg-slate-50 max-w-4xl h-[90vh] overflow-hidden shadow-2xl flex flex-col text-left">
          {selectedTicket && (
            <>
                <div className={`p-8 text-white flex items-center justify-between shrink-0 ${selectedTicket.userType === 'technician' ? 'bg-orange-600' : 'bg-blue-600'}`}>
                    <div className="flex items-center gap-4 text-left">
                        <LifeBuoy size={28} className="animate-spin-slow" />
                        <div className="text-left">
                            <h2 className="text-xl font-black uppercase italic text-white">Ticket #{selectedTicket.protocol}</h2>
                            <p className="text-[10px] font-black uppercase italic opacity-80 text-white">{selectedTicket.userName} • {selectedTicket.category.toUpperCase()}</p>
                        </div>
                    </div>
                    <Button variant="ghost" onClick={() => setSelectedTicket(null)} className="text-white hover:bg-white/10 rounded-full h-10 w-10 p-0"><X size={20}/></Button>
                </div>

                <div className="flex-1 overflow-y-auto p-10 space-y-8 bg-slate-50 text-left no-scrollbar">
                    {/* FOTOS INICIAIS DO CHAMADO */}
                    {selectedTicket.attachmentUrls?.length > 0 && (
                        <div className="bg-white p-6 rounded-[2rem] border border-slate-200 shadow-sm mb-6">
                            <Label className="text-blue-600 mb-4 flex items-center gap-2 italic font-black uppercase text-[10px]"><ImageIcon size={14}/> Evidências Iniciais do Usuário:</Label>
                            <div className="flex flex-wrap gap-4">
                                {selectedTicket.attachmentUrls.map((url, idx) => (
                                    <a key={idx} href={url} target="_blank" rel="noreferrer" className="w-24 h-24 rounded-2xl overflow-hidden border-2 border-slate-100 hover:border-blue-500 transition-all shadow-md">
                                        <img src={url} className="w-full h-full object-cover" alt="Anexo" />
                                    </a>
                                ))}
                            </div>
                        </div>
                    )}

                    {chatMessages.map((msg, i) => {
                        const isAdmin = msg.senderType === 'admin';
                        return (
                            <div key={i} className={`animate-in fade-in slide-in-from-bottom-4 duration-300 text-left`}>
                                <div className={`p-6 rounded-[2rem] shadow-sm border ${isAdmin ? 'bg-blue-600 text-white border-blue-500 ml-16' : 'bg-white text-slate-800 border-slate-100 mr-16'}`}>
                                    <div className="flex items-center justify-between mb-4 border-b pb-2 opacity-80">
                                        <span className="text-[10px] font-black uppercase italic">{msg.senderName}</span>
                                        <span className="text-[9px] font-black uppercase italic">{msg.createdAt?.toDate ? msg.createdAt.toDate().toLocaleString('pt-BR') : 'Agora'}</span>
                                    </div>
                                    <p className="text-sm font-bold italic leading-relaxed whitespace-pre-wrap">"{msg.text}"</p>
                                    
                                    {/* FOTOS ENVIADAS NA RÉPLICA */}
                                    {msg.attachmentUrls?.length > 0 && (
                                        <div className="mt-4 flex flex-wrap gap-2">
                                            {msg.attachmentUrls.map((url, idx) => (
                                                <a key={idx} href={url} target="_blank" rel="noreferrer" className="w-20 h-20 rounded-xl overflow-hidden border-2 border-white/30 shadow-sm">
                                                    <img src={url} className="w-full h-full object-cover" alt="Anexo Mensagem" />
                                                </a>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                    <div ref={chatEndRef} />
                </div>

                <div className="p-8 bg-white border-t border-slate-100 shrink-0 text-left">
                    <div className="space-y-4">
                        <div className="flex items-center justify-between px-4">
                            <label className="text-[9px] font-black uppercase text-slate-400 italic">Responder Chamado</label>
                            <div className="flex items-center gap-2">
                              <input type="file" multiple className="hidden" ref={replyFileInputRef} onChange={(e) => {
                                  const files = Array.from(e.target.files);
                                  setReplyImages(prev => [...prev, ...files]);
                                  setReplyPreviews(prev => [...prev, ...files.map(f => URL.createObjectURL(f))]);
                              }} accept="image/*" />
                              <Button type="button" onClick={() => replyFileInputRef.current.click()} variant="ghost" className="h-10 text-[9px] font-black uppercase italic gap-2 text-blue-600 border border-blue-50 px-4 rounded-xl">
                                  <Camera size={14} /> Anexar Prova
                              </Button>
                              {selectedTicket.status !== 'completed' && (
                                <Button onClick={() => handleUpdateStatus(selectedTicket.id, 'completed', selectedTicket.userId, selectedTicket.protocol)} className="h-10 bg-green-600 text-white rounded-xl font-black italic text-[9px] px-4">FINALIZAR</Button>
                              )}
                            </div>
                        </div>
                        <div className="flex gap-4 items-start">
                          <div className="flex-1 space-y-2">
                              <Textarea value={replyText} onChange={(e) => setReplyText(e.target.value)} placeholder="ESCREVA SUA RESPOSTA..." className="h-24 rounded-2xl bg-slate-50 border-none font-black uppercase italic px-8 py-5 shadow-inner text-left resize-none" />
                              {replyPreviews.length > 0 && (
                                  <div className="flex flex-wrap gap-2">
                                      {replyPreviews.map((url, i) => (
                                          <div key={i} className="relative w-14 h-14 rounded-xl overflow-hidden border-2 border-slate-100 shadow-md">
                                              <img src={url} className="w-full h-full object-cover" />
                                              <button onClick={() => { setReplyImages(prev => prev.filter((_, idx) => idx !== i)); setReplyPreviews(prev => prev.filter((_, idx) => idx !== i)); }} className="absolute inset-0 bg-red-600/80 text-white opacity-0 hover:opacity-100 flex items-center justify-center transition-all"><X size={14}/></button>
                                          </div>
                                      ))}
                                  </div>
                              )}
                          </div>
                          <Button onClick={handleSendAdminReply} className="h-24 w-24 rounded-[2rem] bg-slate-900 text-white shadow-2xl flex flex-col items-center justify-center gap-1">
                              <Reply size={24} /> <span className="text-[8px] font-black uppercase">Responder</span>
                          </Button>
                        </div>
                    </div>
                </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const TicketCard = ({ ticket, onOpenChat, onComplete }) => {
  const isTech = ticket.userType === 'technician';
  return (
    <Card className="rounded-[2.5rem] border-none shadow-xl overflow-hidden transition-all hover:scale-[1.005] bg-white group relative text-left">
      <div className={`absolute left-0 top-0 bottom-0 w-2 ${isTech ? 'bg-orange-500' : 'bg-blue-600'}`} />
      <CardContent className="p-7 flex flex-col lg:flex-row items-center justify-between gap-6">
        <div className="flex items-start gap-6 flex-1 w-full text-left">
          <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shrink-0 ${isTech ? 'bg-orange-50 text-orange-600' : 'bg-blue-50 text-blue-600'}`}>
            {ticket.category === 'os_issue' ? <Wrench size={24} /> : <MessageSquare size={24} />}
          </div>
          <div className="flex-1 text-left">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <Badge className={`${ticket.status === 'completed' ? 'bg-green-600' : ticket.status === 'responded' ? 'bg-blue-500' : 'bg-red-500'} text-[7px] font-black uppercase italic border-none text-white px-3 py-1 shadow-sm`}>{ticket.status || 'Pendente'}</Badge>
              <Badge variant="outline" className={`${isTech ? 'border-orange-200 text-orange-600' : 'border-blue-200 text-blue-600'} text-[7px] font-black uppercase italic`}>{isTech ? 'Profissional' : 'Cliente'}</Badge>
              <span className="text-[9px] font-black text-slate-300 uppercase ml-auto italic">PROT: {ticket.protocol}</span>
            </div>
            <h3 className="text-lg font-black text-slate-900 uppercase italic leading-tight mb-2 text-left">{ticket.subject}</h3>
            <div className="flex items-center gap-4 text-[10px] font-black text-slate-400 uppercase italic">
              <div className="flex items-center gap-1.5"><User size={12} className={isTech ? 'text-orange-500' : 'text-blue-500'} /> {ticket.userName}</div>
              {ticket.attachmentUrls?.length > 0 && <div className="flex items-center gap-1.5 text-blue-500"><ImageIcon size={12}/> {ticket.attachmentUrls.length} Fotos anexadas</div>}
            </div>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 w-full lg:w-auto shrink-0">
          {ticket.status !== 'completed' && (
            <Button onClick={onComplete} className="h-14 bg-green-600 hover:bg-green-700 text-white font-black rounded-2xl uppercase italic text-[10px] px-8 shadow-lg active:scale-95 transition-all">CONCLUIR</Button>
          )}
          <Button onClick={onOpenChat} className={`h-14 px-8 rounded-2xl font-black uppercase italic text-[10px] shadow-lg active:scale-95 flex items-center gap-2 ${ticket.status === 'pending' ? 'bg-slate-900 text-white' : 'bg-white text-slate-900 border border-slate-200'}`}>
            <MessageSquare size={16} /> ATENDER
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const Label = ({ children, className }) => <label className={`block font-black uppercase text-[10px] ${className}`}>{children}</label>;

export default AdminSupport;